import React from 'react';
import PropTypes from 'prop-types';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import Carousel from 'components/Carousel';
import { CARD_GAP, CARD_WIDTH } from 'constants/productCard';
import { Text, Box } from 'components/ui';
import Storage from 'utils/localStorage/Storage';
import contentConsts from 'constants/content';
import { sendCmsComponentEvent } from 'analytics/utils/cmsComponents';
import { firePuwyloBatchCardSotEvent } from 'analytics/utils/puwyloBatchSotTracking';
import anaConsts from 'analytics/constants';
import RichText from 'components/Content/RichText';
import PuwyloHomepageUtils from 'utils/PuwyloHomepageUtils';

const {
    RECAP_CAROUSEL: { ITEM_URLS },
    CUSTOM_ACTION_TYPES
} = contentConsts;
const {
    CMS_COMPONENT_EVENTS: { ITEM_CLICK }
} = anaConsts;

class Recap extends BaseClass {
    controlReplenImpressionFired = false;
    controlAnonymousImpressionFired = false;
    controlKeepShoppingImpressionFired = false;
    controlDealsImpressionFired = false;
    // PME-1959: RTCO/Replen cards render after the carousel's one-shot impression on cold
    // load, so their SOT/EVT impression is fired imperatively here — once per user session.
    rtcoImpressionFired = false;
    replenImpressionFired = false;
    // PDE-2619: the Keep Shopping For (Card 1) card mounts from the async PUWYLO batch after the
    // carousel's one-shot impression, so its SOT impression is fired imperatively here too.
    keepShoppingImpressionFired = false;
    dealsImpressionFired = false;
    newMarkdownsImpressionFired = false;

    componentDidMount() {
        const { requiredData = {} } = this.props;

        this.props.fetchPurchaseHistory(this.props);
        this.props.fetchBeautyRecommendations(this.props);
        requiredData.rvData && this.props.updateRvData(Storage.local.getItem('rvData'));

        this.captureAndFetchPuwyloBatch();

        this.fetchRtcoDataIfEnabled();
        this.maybeFireControlReplenImpression();
        this.maybeFireControlAnonymousImpression();
        this.maybeFireControlKeepShoppingImpression();
        this.maybeFireControlDealsImpression();
        this.maybeFireRtcoImpression();
        this.maybeFireReplenImpression();
        this.maybeFireKeepShoppingImpression();
        this.maybeFireDealsImpression();
        this.maybeFireNewMarkdownsImpression();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.userId !== this.props.userId) {
            this.props.fetchPurchaseHistory(this.props);
            this.props.fetchBeautyRecommendations(this.props);

            // PDE-2618: re-capture + re-fetch the PUWYLO batch for the new user (sign-in).
            this.captureAndFetchPuwyloBatch();

            this.fetchRtcoDataIfEnabled();
            this.controlReplenImpressionFired = false;
            this.controlAnonymousImpressionFired = false;
            this.controlKeepShoppingImpressionFired = false;
            this.controlDealsImpressionFired = false;
            this.rtcoImpressionFired = false;
            this.replenImpressionFired = false;
            this.keepShoppingImpressionFired = false;
            this.dealsImpressionFired = false;
            this.newMarkdownsImpressionFired = false;
        } else if (!prevProps.isInitialized && this.props.isInitialized) {
            this.captureAndFetchPuwyloBatch();
        }

        this.maybeFireControlReplenImpression();
        this.maybeFireControlAnonymousImpression();
        this.maybeFireControlKeepShoppingImpression();
        this.maybeFireControlDealsImpression();
        this.maybeFireRtcoImpression();
        this.maybeFireReplenImpression();
        this.maybeFireKeepShoppingImpression();
        this.maybeFireDealsImpression();
        this.maybeFireNewMarkdownsImpression();

        if (prevProps.isAnonymous && !this.props.isAnonymous) {
            this.props.clearRecapAbTestData();
        }
    }

    // PDE-2618: killswitch-gated rvCategoryData capture + batch fetch. Capture must
    // precede the fetch because the batch client reads rvCategoryData from localStorage.
    captureAndFetchPuwyloBatch() {
        if (!PuwyloHomepageUtils.isPuwyloHomepageExpansionEnabled()) {
            return;
        }

        if (!this.props.isInitialized) {
            return;
        }

        this.props.updateRvCategoryData();
        this.props
            .fetchPuwyloBatchData(this.props)
            .then(descriptors => {
                (descriptors || []).forEach(d => {
                    firePuwyloBatchCardSotEvent(d.event, { sid: d.sid, title: d.title, publishedVersion: d.publishedVersion });
                });
            })
            .catch(() => {}); // thunk catches its own errors and resolves; this is defensive only
    }

    maybeFireControlReplenImpression() {
        if (this.controlReplenImpressionFired) {
            return;
        }

        if (this.props.hasControlReplenImpression && this.props.triggerControlReplenImpression) {
            this.props.triggerControlReplenImpression();
            this.controlReplenImpressionFired = true;
        }
    }
    // PUWLO Unrecognized — control-group impression fires once per session
    // even though the carousel is hidden. Mirrors maybeFireControlReplenImpression
    // so anonymous Control bucket measurement matches Challenger.
    maybeFireControlAnonymousImpression() {
        if (this.controlAnonymousImpressionFired) {
            return;
        }

        if (this.props.hasControlAnonymousImpression && this.props.triggerControlAnonymousImpression) {
            this.props.triggerControlAnonymousImpression();
            this.controlAnonymousImpressionFired = true;
        }
    }

    // Keep Shopping For Control group impression — card is hidden but
    // impression must fire for analytics measurement parity with Challenger group.
    // Mirrors maybeFireControlReplenImpression for PUWYLO expansion AB test.
    maybeFireControlKeepShoppingImpression() {
        if (this.controlKeepShoppingImpressionFired) {
            return;
        }

        if (this.props.hasControlKeepShoppingImpression && this.props.triggerControlKeepShoppingImpression) {
            this.props.triggerControlKeepShoppingImpression();
            this.controlKeepShoppingImpressionFired = true;
        }
    }

    maybeFireControlDealsImpression() {
        if (this.controlDealsImpressionFired) {
            return;
        }

        if (this.props.hasControlDealsImpression && this.props.triggerControlDealsImpression) {
            this.props.triggerControlDealsImpression();
            this.controlDealsImpressionFired = true;
        }
    }

    // PME-1959: fire the RTCO (BASKET) card impression once its card is rendered with settled
    // data, since the carousel's one-shot impression already latched before it mounted.
    maybeFireRtcoImpression() {
        if (this.rtcoImpressionFired) {
            return;
        }

        if (this.props.hasRtcoImpression && this.props.triggerRtcoImpression) {
            this.props.triggerRtcoImpression();
            this.rtcoImpressionFired = true;
        }
    }

    // PME-1959: fire the Replen (REPLENISHMENT) card impression once its card is rendered with
    // settled data. Mirrors maybeFireRtcoImpression so both cards fire once per user session.
    maybeFireReplenImpression() {
        if (this.replenImpressionFired) {
            return;
        }

        if (this.props.hasReplenImpression && this.props.triggerReplenImpression) {
            this.props.triggerReplenImpression();
            this.replenImpressionFired = true;
        }
    }

    maybeFireKeepShoppingImpression() {
        if (this.keepShoppingImpressionFired) {
            return;
        }

        if (this.props.hasKeepShoppingImpression && this.props.triggerKeepShoppingImpression) {
            this.props.triggerKeepShoppingImpression();
            this.keepShoppingImpressionFired = true;
        }
    }

    maybeFireDealsImpression() {
        if (this.dealsImpressionFired) {
            return;
        }

        if (this.props.hasDealsImpression && this.props.triggerDealsImpression) {
            this.props.triggerDealsImpression();
            this.dealsImpressionFired = true;
        }
    }

    maybeFireNewMarkdownsImpression() {
        if (this.newMarkdownsImpressionFired) {
            return;
        }

        if (this.props.hasNewMarkdownsImpression && this.props.triggerNewMarkdownsImpression) {
            this.props.triggerNewMarkdownsImpression();
            this.newMarkdownsImpressionFired = true;
        }
    }

    fetchRtcoDataIfEnabled() {
        const basketItem = this.props?.items?.find(item => item?.action?.targetUrl === ITEM_URLS.BASKET);

        if (basketItem) {
            const { personalization } = basketItem;
            const isEnabled = personalization?.isEnabled;
            const isRtcoEnabled = personalization?.isRtcoEnabled;
            const context = personalization?.context;

            if (isEnabled && isRtcoEnabled && context) {
                this.props.fetchRtcoP13nData({ contextId: context });
            }
        }
    }

    handleAnonymousSubtitleClick = e => {
        if (e.target.getAttribute('role') !== 'button') {
            return;
        }

        const { anonymousSubtitle, title, sid } = this.props;
        const entries = anonymousSubtitle?.text?.links?.entries?.inline || [];
        const isSignInAction = entries.some(entry => entry?.action?.actionType === CUSTOM_ACTION_TYPES.SIGN_IN);

        if (isSignInAction) {
            sendCmsComponentEvent({
                items: [{ ...anonymousSubtitle, itemIndex: 0 }],
                eventName: ITEM_CLICK,
                title,
                sid,
                component: anonymousSubtitle?.type
            });
        }
    };

    render() {
        const {
            sid = null,
            cards = null,
            title = null,
            subtitle = null,
            hideRow = false,
            isLoading = true,
            triggerRecapImpression = null,
            isAnonymous = false,
            anonymousSubtitle = null
        } = this.props;

        if (hideRow) {
            return null;
        }

        return (
            <div id={sid}>
                {title && (
                    <Text
                        is='h2'
                        lineHeight='tight'
                        marginBottom={subtitle || anonymousSubtitle?.text ? 1 : 4}
                        fontSize={['md', 'lg']}
                        fontWeight='bold'
                        children={title}
                    />
                )}
                {subtitle && (
                    <Text
                        is='p'
                        lineHeight='tight'
                        marginBottom={4}
                        marginRight='auto'
                        children={subtitle}
                    />
                )}
                {isAnonymous && anonymousSubtitle?.text && (
                    <Box
                        marginBottom={2}
                        onClickCapture={this.handleAnonymousSubtitleClick}
                    >
                        <RichText content={anonymousSubtitle?.text} />
                    </Box>
                )}
                <Carousel
                    isLoading={isLoading}
                    gap={CARD_GAP}
                    paddingY={4}
                    marginX='-container'
                    scrollPadding={[2, 'container']}
                    itemWidth={CARD_WIDTH}
                    items={cards}
                    hasShadowHack={true}
                    onImpression={triggerRecapImpression}
                />
            </div>
        );
    }
}

Recap.propTypes = {
    sid: PropTypes.string,
    cards: PropTypes.array,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    hideRow: PropTypes.bool,
    isLoading: PropTypes.bool,
    componentList: PropTypes.array,
    items: PropTypes.array,
    fetchRtcoP13nData: PropTypes.func,
    fetchPurchaseHistory: PropTypes.func,
    fetchBeautyRecommendations: PropTypes.func,
    updateRvData: PropTypes.func,
    updateRvCategoryData: PropTypes.func,
    fetchPuwyloBatchData: PropTypes.func,
    requiredData: PropTypes.object,
    triggerRecapImpression: PropTypes.func,
    hasRtcoImpression: PropTypes.bool,
    triggerRtcoImpression: PropTypes.func,
    hasReplenImpression: PropTypes.bool,
    triggerReplenImpression: PropTypes.func,
    hasKeepShoppingImpression: PropTypes.bool,
    triggerKeepShoppingImpression: PropTypes.func,
    hasDealsImpression: PropTypes.bool,
    triggerDealsImpression: PropTypes.func,
    hasNewMarkdownsImpression: PropTypes.bool,
    triggerNewMarkdownsImpression: PropTypes.func,
    hasControlReplenImpression: PropTypes.bool,
    triggerControlReplenImpression: PropTypes.func,
    hasControlAnonymousImpression: PropTypes.bool,
    triggerControlAnonymousImpression: PropTypes.func,
    hasControlKeepShoppingImpression: PropTypes.bool,
    triggerControlKeepShoppingImpression: PropTypes.func,
    hasControlDealsImpression: PropTypes.bool,
    triggerControlDealsImpression: PropTypes.func,
    isAnonymous: PropTypes.bool,
    isInitialized: PropTypes.bool,
    anonymousSubtitle: PropTypes.object,
    clearRecapAbTestData: PropTypes.func
};

export default wrapComponent(Recap, 'Recap', true);
